<script setup lang="ts">
import { $getRoot, $parseSerializedNode, type LexicalNode, type RangeSelection } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted } from "vue";

import { $createDartLinkNode } from "~/components/text/nodes/DartLinkNode";
import { $createEntityNode, EntityNode } from "~/components/text/nodes/EntityNode";
import { getDartboardUrl, getDocUrl, getFolderUrl, getTaskUrl, getViewUrl } from "~/router/common";
import { EntityName, TypeaheadSpecialEntity } from "~/shared/enums";
import type { Skill, TypeaheadOption } from "~/shared/types";
import { useDataStore } from "~/stores";

import { getDocOptions, getSkillOptionsSync, getTaskOptions, MINOR_ENTITY_OPTION_FNS } from "./links";
import TypeaheadPlugin from "./TypeaheadPlugin.vue";

const dataStore = useDataStore();
const editor = useLexicalComposer();

const insertNode = (node: LexicalNode, selection: RangeSelection) => {
  selection?.insertNodes([node]);
  selection?.insertRawText(" ");
};

const insertSkill = (skill: Skill, selection?: RangeSelection) => {
  editor.update(() => {
    const root = $getRoot();
    skill.prompt.root.children.forEach((child) => {
      insertNode($parseSerializedNode(child), selection ?? root.selectEnd());
    });
  });
};

const typeahead = computed(() => ({
  trigger: "@",
  getOptions: [async () => getSkillOptionsSync(), getTaskOptions, getDocOptions, ...MINOR_ENTITY_OPTION_FNS],
}));

const onSelect = async (selection: RangeSelection, option: TypeaheadOption) => {
  if (typeof option.value === "string" && option.specialEntity) {
    switch (option.specialEntity) {
      case TypeaheadSpecialEntity.TASK: {
        const task = await dataStore.getTaskByDuid(option.value);
        if (!task) {
          return;
        }
        editor.update(() => {
          insertNode($createDartLinkNode(getTaskUrl(task), EntityName.TASK, task.duid), selection);
        });
        break;
      }
      case TypeaheadSpecialEntity.DOC: {
        const doc = await dataStore.getDocByDuid(option.value);
        if (!doc) {
          return;
        }
        editor.update(() => {
          insertNode($createDartLinkNode(getDocUrl(doc), EntityName.DOC, doc.duid), selection);
        });
        break;
      }
      case TypeaheadSpecialEntity.VIEW: {
        const view = dataStore.getViewByDuid(option.value);
        if (!view) {
          return;
        }
        editor.update(() => {
          insertNode($createDartLinkNode(getViewUrl(view), EntityName.VIEW, view.duid), selection);
        });
        break;
      }
      case TypeaheadSpecialEntity.DARTBOARD: {
        const dartboard = dataStore.getDartboardByDuid(option.value);
        const space = dataStore.getSpaceByDuid(dartboard?.spaceDuid ?? "");
        if (!dartboard || !space) {
          return;
        }
        editor.update(() => {
          insertNode(
            $createDartLinkNode(getDartboardUrl(dartboard, space), EntityName.DARTBOARD, dartboard.duid),
            selection
          );
        });
        break;
      }
      case TypeaheadSpecialEntity.FOLDER: {
        const folder = dataStore.getFolderByDuid(option.value);
        const space = dataStore.getSpaceByDuid(folder?.spaceDuid ?? "");
        if (!folder || !space) {
          return;
        }
        editor.update(() => {
          insertNode($createDartLinkNode(getFolderUrl(folder, space), EntityName.FOLDER, folder.duid), selection);
        });
        break;
      }
      case TypeaheadSpecialEntity.SKILL: {
        const skill = dataStore.getSkillByDuid(option.value);
        if (!skill) {
          return;
        }
        insertSkill(skill, selection);

        break;
      }
      default: {
        break;
      }
    }
  }

  if (!option.property) {
    return;
  }

  const entityNode = $createEntityNode(option.property.duid, option.value);
  insertNode(entityNode, selection);
};

defineExpose({
  insertSkill,
});

onMounted(() => {
  if (!editor.hasNodes([EntityNode])) {
    throw new Error("AiChatTypeaheadPlugin: EntityNode not registered on editor");
  }
});
</script>

<template>
  <TypeaheadPlugin
    :key="typeahead.trigger"
    :trigger="typeahead.trigger"
    :get-options-fns="typeahead.getOptions"
    wide
    @select="onSelect" />
</template>
